import { AbstractControl, FormGroup, ValidationErrors } from "@angular/forms";

/**
 * Utilities for control-level cross-field validation in `ez-form` input configs.
 *
 * How this differs from `groupValidators`:
 * - Cross validators attach errors to a specific control and declare explicit `dependsOn` fields.
 * - `groupValidators` run at the `FormGroup` level and attach errors to the group unless you manually map errors to controls.
 * - Use cross validators when the UX should show an inline error on one field based on sibling values.
 * - Use `groupValidators` for whole-form rules/invariants where no single field owns the error.
 */

/** Predicate used by `mustSatisfy` to evaluate a control value against sibling values. */
export type ComparisonFn = (self: unknown, ...peers: unknown[]) => boolean;

/**
 * Builds an `ez-form` cross-validator descriptor.
 *
 * The returned object is intended for `InputConfig.crossValidators` and evaluates
 * the current control value against one or more dependency control values.
 */
export function mustSatisfy(
  dependsOn: string[],
  compare: ComparisonFn,
  errorKey: string,
  message?: string
) {
  return {
    dependsOn,
    errorKey,
    message,
    validator(control: AbstractControl, group: FormGroup): ValidationErrors | null {
      const peers = dependsOn.map(name => group.get(name)?.value);
      return compare(control.value, ...peers) ? null : { [errorKey]: true };
    },
  };
}

/** Coerces supported date-like values to `Date`, otherwise returns null. */
function coerceDate(value: any): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Cross-validator requiring the current control date to be on/before another date field.
 *
 * Common use: attach to a start-date input with `crossValidators: [startBeforeEnd('endDate')]`.
 */
export const startBeforeEnd = (endField: string) =>
  mustSatisfy(
    [endField],
    (start, end) => {
      const startDate = coerceDate(start);
      const endDate = coerceDate(end);
      if (!startDate || !endDate) return true;
      return startDate <= endDate;
    },
    'dateRange',
    'Start date must be on or before the end date.'
  );

/**
 * Cross-validator requiring the current control when another field equals a specific value.
 *
 * Common use: "when `documentType` is `OTHER`, `otherDescription` is required".
 *
 * ```ts
 * {
 *   name: "otherDescription",
 *   type: "text",
 *   crossValidators: [
 *     requiredWhenEquals("documentType", "OTHER", "requiredWhenOther"),
 *   ],
 *   validationMessages: {
 *     requiredWhenOther: "Description is required when document type is Other",
 *   },
 * }
 * ```
 */
export const requiredWhenEquals = (
  dependencyField: string,
  expectedDependencyValue: unknown,
  errorKey: string = 'required'
) =>
  mustSatisfy(
    [dependencyField],
    (self, dependencyValue) => {
      if (dependencyValue !== expectedDependencyValue) {
        return true;
      }

      if (typeof self === 'string') {
        return self.trim().length > 0;
      }

      if (Array.isArray(self)) {
        return self.length > 0;
      }

      return self !== null && self !== undefined;
    },
    errorKey
  );

/**
 * Cross-validator requiring the current control when a dependency predicate evaluates true.
 *
 * Use this when equality checks are not enough (for example, object-shaped dependency values
 * like file chooser payloads).
 */
export const requiredWhen = (
  dependencyField: string,
  shouldRequire: (dependencyValue: unknown) => boolean,
  errorKey: string = 'required'
) =>
  mustSatisfy(
    [dependencyField],
    (self, dependencyValue) => {
      if (!shouldRequire(dependencyValue)) {
        return true;
      }

      if (typeof self === 'string') {
        return self.trim().length > 0;
      }

      if (Array.isArray(self)) {
        return self.length > 0;
      }

      return self !== null && self !== undefined;
    },
    errorKey
  );
